/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  getKnowledgeGraphEvents,
  listMarketIntelligenceItems,
  listMarketStateSnapshots,
} from "../../api/api";
import PageBackButton from "../../components/PageBackButton";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import {
  Activity,
  BarChart3,
  BrainCircuit,
  ChevronLeft,
  ChevronRight,
  Clipboard,
  Clock3,
  ExternalLink,
  Layers,
  RefreshCw,
  Search,
  Target,
  TrendingDown,
  TrendingUp,
} from "lucide-react";

const TARGET_TYPES = [
  { value: "", label: "All targets" },
  { value: "CURRENCY", label: "Currencies" },
  { value: "COMMODITY", label: "Commodities" },
  { value: "INDEX", label: "Indices" },
  { value: "SYMBOL", label: "Symbols" },
  { value: "COUNTRY", label: "Countries" },
  { value: "SECTOR", label: "Sectors" },
  { value: "THEME", label: "Themes" },
  { value: "ENTITY", label: "Entities" },
];

const FRESHNESS_OPTIONS = [
  { value: "", label: "All freshness" },
  { value: "false", label: "Fresh only" },
  { value: "true", label: "Stale only" },
];

const CONFIDENCE_OPTIONS = [
  { value: "", label: "All confidence" },
  { value: "0.5", label: "0.50+" },
  { value: "0.7", label: "0.70+" },
  { value: "0.85", label: "0.85+" },
];

const TIMEFRAME_OPTIONS = [
  { value: "", label: "All timeframes" },
  { value: "latest", label: "Latest" },
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "14d", label: "14 days" },
  { value: "30d", label: "30 days" },
];

const ORDERING_OPTIONS = [
  { value: "-computed_at", label: "Newest computed" },
  { value: "computed_at", label: "Oldest computed" },
  { value: "-confidence", label: "Highest confidence" },
  { value: "confidence", label: "Lowest confidence" },
  { value: "-sentiment", label: "Most bullish" },
  { value: "sentiment", label: "Most bearish" },
  { value: "target", label: "Target A-Z" },
];

const DETAIL_TABS = [
  { id: "overview", label: "Overview", icon: BrainCircuit },
  { id: "signals", label: "Signals", icon: Activity },
  { id: "changes", label: "Changes", icon: RefreshCw },
  { id: "evidence", label: "Evidence", icon: Layers },
];

const selectClassName =
  "w-full rounded-md border border-borderDefault bg-backgroundInput px-3 py-2 text-sm text-textPrimary outline-none focus:border-primary focus:ring-1 focus:ring-primary";

function parsePositiveInt(value, fallback = 1) {
  const number = Number.parseInt(value, 10);
  return Number.isFinite(number) && number > 0 ? number : fallback;
}

function optionValueOrDefault(options, value, fallback = "") {
  return options.some((option) => option.value === value) ? value : fallback;
}

function labelize(value) {
  if (!value) return "-";
  return String(value)
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatScore(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(2) : "-";
}

function formatSignedNumber(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${number > 0 ? "+" : ""}${number.toFixed(2)}` : "-";
}

function formatPercent(value) {
  const number = Number(value);
  return Number.isFinite(number) ? `${Math.round(number * 100)}%` : "-";
}

function formatTime(ts) {
  try {
    if (!ts) return "-";
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return ts || "-";
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return ts || "-";
  }
}

function formatAge(ts) {
  if (!ts) return "-";
  const date = new Date(ts);
  if (Number.isNaN(date.getTime())) return "-";
  const diffMs = Date.now() - date.getTime();
  const absMs = Math.abs(diffMs);
  if (absMs < 60 * 1000) return "now";
  const units = [
    { label: "d", ms: 24 * 60 * 60 * 1000 },
    { label: "h", ms: 60 * 60 * 1000 },
    { label: "m", ms: 60 * 1000 },
  ];
  const unit = units.find((entry) => absMs >= entry.ms) || units[units.length - 1];
  const value = Math.max(1, Math.round(absMs / unit.ms));
  return diffMs >= 0 ? `${value}${unit.label} ago` : `in ${value}${unit.label}`;
}

function stripHtml(html) {
  if (!html || typeof document === "undefined") return html || "";
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || "";
}

function snapshotRouteKey(snapshot, index = 0) {
  return `market-state-${snapshot?.id || snapshot?.target_key || snapshot?.target_name || index}`;
}

function getSnapshotBias(snapshot) {
  const thesis = snapshot?.active_thesis || {};
  if (thesis.current_bias) return thesis.current_bias;
  const sentiment = Number(snapshot?.sentiment_score);
  if (Number.isFinite(sentiment)) {
    if (sentiment >= 0.08) return "BULLISH";
    if (sentiment <= -0.08) return "BEARISH";
  }
  return "NEUTRAL";
}

function biasTextClass(bias) {
  if (bias === "BULLISH") return "text-success";
  if (bias === "BEARISH") return "text-destructive";
  if (bias === "MIXED") return "text-warning";
  return "text-textPrimary";
}

function biasBadgeClass(bias) {
  const base = "rounded-full px-2 py-0.5 text-[11px] font-semibold";
  if (bias === "BULLISH") return `${base} bg-success/15 text-success`;
  if (bias === "BEARISH") return `${base} bg-destructive/15 text-destructive`;
  if (bias === "MIXED") return `${base} bg-warning/15 text-warning`;
  return `${base} bg-secondary/40 text-textSecondary`;
}

function freshnessBadgeClass(isStale) {
  const base = "rounded-full px-2 py-0.5 text-[11px] font-semibold";
  return isStale ? `${base} bg-warning/15 text-warning` : `${base} bg-success/15 text-success`;
}

function thesisStatusBadgeClass(status) {
  const base = "rounded-full px-2 py-0.5 text-[11px] font-semibold";
  if (status === "ACTIVE") return `${base} bg-success/15 text-success`;
  if (status === "WEAKENING") return `${base} bg-warning/15 text-warning`;
  if (status === "CONTRADICTED") return `${base} bg-destructive/15 text-destructive`;
  return `${base} bg-secondary/40 text-textSecondary`;
}

function snapshotSectionStyle(tone) {
  const colorVarByTone = {
    bullish: "--color-success",
    bearish: "--color-destructive",
    risk: "--color-warning",
    catalyst: "--color-info",
  };
  const colorVar = colorVarByTone[tone];
  if (!colorVar) return undefined;
  return {
    borderColor: `color-mix(in srgb, var(${colorVar}) 36%, transparent)`,
    backgroundColor: `color-mix(in srgb, var(${colorVar}) 10%, transparent)`,
  };
}

function snapshotItemRank(item) {
  const materiality = Number(item?.materiality_score);
  const confidence = Number(item?.confidence);
  const impact = Math.abs(Number(item?.impact_magnitude));
  return (
    (Number.isFinite(materiality) ? materiality * 3 : 0) +
    (Number.isFinite(confidence) ? confidence * 2 : 0) +
    (Number.isFinite(impact) ? impact : 0)
  );
}

function sortSnapshotItems(items) {
  return [...(Array.isArray(items) ? items : [])].sort((a, b) => snapshotItemRank(b) - snapshotItemRank(a));
}

function uniquePositiveIds(values) {
  const ids = [];
  const seen = new Set();
  values.forEach((value) => {
    const number = Number.parseInt(value, 10);
    if (!Number.isFinite(number) || number <= 0 || seen.has(number)) return;
    seen.add(number);
    ids.push(number);
  });
  return ids;
}

function collectSnapshotEvidenceIds(snapshot, thesis, stateChange) {
  const eventValues = [
    ...(Array.isArray(snapshot?.key_event_ids) ? snapshot.key_event_ids : []),
    ...(Array.isArray(thesis?.supporting_event_ids) ? thesis.supporting_event_ids : []),
    ...(Array.isArray(thesis?.contradicting_event_ids) ? thesis.contradicting_event_ids : []),
    ...(Array.isArray(stateChange?.trigger_event_ids) ? stateChange.trigger_event_ids : []),
  ];
  const sourceValues = [...(Array.isArray(snapshot?.source_item_ids) ? snapshot.source_item_ids : [])];

  [
    snapshot?.recent_changes,
    snapshot?.bullish_factors,
    snapshot?.bearish_factors,
    snapshot?.active_risks,
    snapshot?.upcoming_catalysts,
  ].forEach((items) => {
    if (!Array.isArray(items)) return;
    items.forEach((item) => {
      eventValues.push(item?.event_id, item?.market_event_id);
      sourceValues.push(item?.source_item_id, item?.raw_item_id);
    });
  });

  return {
    eventIds: uniquePositiveIds(eventValues),
    sourceIds: uniquePositiveIds(sourceValues),
  };
}

function knowledgeGraphUrlForSnapshot(snapshot) {
  const params = new URLSearchParams();
  const targetType = String(snapshot?.target_type || "").toUpperCase();
  const targetKey = String(snapshot?.target_key || "").trim();
  const targetName = String(snapshot?.target_name || targetKey || "").trim();

  params.set("from", "market_mind");
  if (targetType === "CURRENCY" && targetKey) {
    params.set("tab", "graph");
    params.set("currency", targetKey.toUpperCase());
  } else if (["SYMBOL", "COMMODITY", "INDEX"].includes(targetType) && targetKey) {
    params.set("tab", "graph");
    params.set("symbol", targetKey.toUpperCase());
  } else if (targetName || targetKey) {
    params.set("tab", "events");
    params.set("search", targetName || targetKey);
  } else {
    params.set("tab", "graph");
  }
  return `/fundamentals/knowledge-graph?${params.toString()}`;
}

function buildSnapshotBrief(snapshot) {
  const thesis = snapshot?.active_thesis || {};
  const llmBrief = getSnapshotLlmBrief(snapshot);
  const snapshotSummary = String(snapshot?.summary || "").trim();
  const thesisDescription = String(thesis.description || "").trim();
  const lines = [
    `${snapshot?.target_name || snapshot?.target_key || "Market Mind"} (${snapshot?.target_key || "-"})`,
    `Bias: ${labelize(thesis.current_bias || getSnapshotBias(snapshot))}`,
    `Confidence: ${formatPercent(thesis.confidence ?? snapshot?.confidence)}`,
    `Sentiment: ${formatSignedNumber(snapshot?.sentiment_score)}`,
    `Computed: ${formatTime(snapshot?.computed_at)}`,
    "",
    "Market brief:",
    llmBrief?.text || thesisDescription || snapshotSummary || "No market brief available.",
  ];
  if (thesis.title) {
    lines.push("", "Active thesis:", thesis.title);
  }
  if (thesisDescription && thesisDescription !== llmBrief?.text) {
    lines.push(thesisDescription);
  }
  if (thesis?.state_change?.reason) {
    lines.push("", `Why changed: ${thesis.state_change.reason}`);
  }
  if (snapshotSummary && snapshotSummary !== llmBrief?.text && snapshotSummary !== thesisDescription) {
    lines.push("", "Snapshot summary:", snapshotSummary);
  }
  return lines.join("\n");
}

function getSnapshotLlmBrief(snapshot) {
  const brief = snapshot?.llm_brief;
  if (!brief || typeof brief !== "object") return null;
  const text = String(brief.text || "").trim();
  if (!text || String(brief.status || "").toLowerCase() !== "success") return null;
  return {
    ...brief,
    text,
    model_name: String(brief.model_name || "").trim(),
    provider_type: String(brief.provider_type || "").trim(),
    generated_at: String(brief.generated_at || "").trim(),
  };
}

function getSnapshotPreviewText(snapshot) {
  const thesis = snapshot?.active_thesis || {};
  const llmBrief = getSnapshotLlmBrief(snapshot);
  return (
    llmBrief?.text ||
    snapshot?.summary ||
    thesis.description ||
    thesis.title ||
    "No summary available."
  );
}

function getSnapshotCounts(snapshot) {
  const metadata = snapshot?.metadata || {};
  const keyEventIds = Array.isArray(snapshot?.key_event_ids) ? snapshot.key_event_ids : [];
  const keyClusterIds = Array.isArray(snapshot?.key_cluster_ids) ? snapshot.key_cluster_ids : [];
  const sourceItemIds = Array.isArray(snapshot?.source_item_ids) ? snapshot.source_item_ids : [];

  return {
    eventCount: snapshot?.event_count ?? metadata.event_count ?? keyEventIds.length,
    clusterCount: snapshot?.cluster_count ?? metadata.cluster_count ?? keyClusterIds.length,
    sourceCount: snapshot?.source_item_count ?? metadata.source_item_count ?? sourceItemIds.length,
  };
}

function MetricCard({ icon: Icon, label, value, detail, tone = "neutral" }) {
  const toneClass =
    tone === "success"
      ? "text-success"
      : tone === "danger"
        ? "text-destructive"
        : tone === "warning"
          ? "text-warning"
          : "text-primary";

  return (
    <div className="rounded-lg border border-borderDefault bg-backgroundSurface px-3 py-2">
      <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase text-textSecondary">
        {Icon ? <Icon className={`h-3.5 w-3.5 ${toneClass}`} /> : null}
        <span>{label}</span>
      </div>
      <div className={`mt-1 text-lg font-semibold ${toneClass}`}>{value}</div>
      {detail ? <div className="mt-0.5 text-[11px] text-textSecondary">{detail}</div> : null}
    </div>
  );
}

function DecisionMetric({ icon: Icon, label, value, detail, valueClassName = "" }) {
  return (
    <div className="rounded-lg border border-borderDefault bg-backgroundBody/50 px-3 py-2">
      <div className="flex items-center gap-1.5 text-[11px] uppercase text-textSecondary">
        {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
        <span>{label}</span>
      </div>
      <div className={`mt-1 text-sm font-semibold ${valueClassName}`}>{value || "-"}</div>
      {detail ? <div className="mt-0.5 text-[11px] text-textSecondary">{detail}</div> : null}
    </div>
  );
}

function SnapshotListItem({ snapshot, index, selected, onSelect }) {
  const thesis = snapshot.active_thesis || {};
  const bias = getSnapshotBias(snapshot);
  const confidence = thesis.confidence ?? snapshot.confidence;
  const counts = getSnapshotCounts(snapshot);
  const targetName = snapshot.target_name || snapshot.target_key || "Untitled target";

  return (
    <button
      type="button"
      onClick={() => onSelect(snapshot, index)}
      aria-current={selected ? "true" : undefined}
      className={[
        "w-full border-l-4 px-3 py-3 text-left transition",
        selected
          ? "border-l-primary bg-primary/10"
          : "border-l-transparent hover:border-l-primary/50 hover:bg-secondary/20",
      ].join(" ")}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-textPrimary">{targetName}</p>
            <span className={biasBadgeClass(bias)}>{labelize(bias)}</span>
          </div>
          <p className="mt-1 truncate text-xs text-textSecondary">
            {getSnapshotPreviewText(snapshot)}
          </p>
        </div>
        <div className="shrink-0 text-right">
          <div className={`text-sm font-semibold ${biasTextClass(bias)}`}>{formatSignedNumber(snapshot.sentiment_score)}</div>
          <div className="text-[11px] text-textSecondary">{formatPercent(confidence)}</div>
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-textSecondary">
        <span>{labelize(snapshot.target_type)}</span>
        <span>{snapshot.target_key || "-"}</span>
        <span>{formatAge(snapshot.computed_at)}</span>
        <span>{counts.eventCount} events</span>
      </div>
    </button>
  );
}

function SnapshotSection({
  title,
  items,
  emptyLabel,
  tone = "neutral",
  variant = "factor",
  limit,
}) {
  const list = sortSnapshotItems(items);
  const visibleList = Number.isFinite(limit) ? list.slice(0, limit) : list;

  return (
    <div
      className="rounded-lg border border-borderDefault bg-secondary/10 p-3"
      style={snapshotSectionStyle(tone)}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-xs font-semibold uppercase text-textSecondary">{title}</h3>
        <span className="text-[11px] text-textSecondary">
          {limit && list.length > visibleList.length ? `Top ${visibleList.length} of ${list.length}` : list.length}
        </span>
      </div>

      {list.length === 0 ? (
        <p className="mt-3 text-sm text-textSecondary">{emptyLabel}</p>
      ) : (
        <div className="mt-3 space-y-2">
          {visibleList.map((item, index) => {
            const itemTitle =
              item?.title || item?.description || item?.target_name || labelize(item?.type || item?.event_type);
            const key =
              item?.event_id ||
              item?.cluster_id ||
              item?.type ||
              item?.title ||
              item?.description ||
              `${title}-${index}`;

            return (
              <div key={key} className="rounded-md bg-backgroundBody/60 px-3 py-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-textPrimary">{itemTitle || "-"}</p>
                    {variant === "factor" && item?.reason ? (
                      <p className="mt-1 text-xs leading-relaxed text-textSecondary">{item.reason}</p>
                    ) : null}
                    {variant === "catalyst" && item?.description ? (
                      <p className="mt-1 text-xs leading-relaxed text-textSecondary">{item.description}</p>
                    ) : null}
                  </div>
                  {item?.url ? (
                    <a
                      href={item.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="shrink-0 text-xs text-primary hover:underline"
                    >
                      Open
                    </a>
                  ) : null}
                </div>

                <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-textSecondary">
                  {item?.source ? <span>{item.source}</span> : null}
                  {item?.timestamp ? <span>{formatTime(item.timestamp)}</span> : null}
                  {item?.event_type ? <span>{labelize(item.event_type)}</span> : null}
                  {variant === "catalyst" && item?.type ? <span>{labelize(item.type)}</span> : null}
                  {Number.isFinite(Number(item?.impact_magnitude)) ? (
                    <span>Impact {formatSignedNumber(item.impact_magnitude)}</span>
                  ) : null}
                  {Number.isFinite(Number(item?.materiality_score)) ? (
                    <span>Materiality {formatScore(item.materiality_score)}</span>
                  ) : null}
                  {Number.isFinite(Number(item?.confidence)) ? (
                    <span>Confidence {formatPercent(item.confidence)}</span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function EvidenceChipList({ label, ids, tone = "neutral", prefix = "#" }) {
  const list = Array.isArray(ids) ? ids : [];
  if (list.length === 0) return null;
  const toneClass =
    tone === "supporting"
      ? "text-success"
      : tone === "contradicting"
        ? "text-destructive"
        : tone === "trigger"
          ? "text-warning"
          : "text-textSecondary";

  return (
    <div>
      <div className={`text-[11px] font-semibold uppercase ${toneClass}`}>{label}</div>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {list.slice(0, 12).map((id) => (
          <span
            key={`${label}-${id}`}
            className="rounded border border-borderDefault bg-backgroundBody/60 px-2 py-0.5 text-[11px] text-textSecondary"
          >
            {prefix}
            {id}
          </span>
        ))}
        {list.length > 12 ? (
          <span className="rounded border border-borderDefault bg-backgroundBody/60 px-2 py-0.5 text-[11px] text-textSecondary">
            +{list.length - 12}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function EvidenceEventCard({ event }) {
  return (
    <div className="rounded-lg border border-borderDefault bg-backgroundBody/60 px-3 py-2">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
              {labelize(event.event_type)}
            </span>
            <span className="text-[11px] text-textSecondary">#{event.id}</span>
          </div>
          <p className="mt-1 text-sm font-medium text-textPrimary">{event.title || "-"}</p>
        </div>
        <span className="shrink-0 text-[11px] text-textSecondary">{formatTime(event.timestamp)}</span>
      </div>
      {event.description ? (
        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-textSecondary">{event.description}</p>
      ) : null}
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-textSecondary">
        {Array.isArray(event.currencies_affected) && event.currencies_affected.length > 0 ? (
          <span>{event.currencies_affected.join(", ")}</span>
        ) : null}
        {Array.isArray(event.symbols) && event.symbols.length > 0 ? (
          <span>{event.symbols.slice(0, 4).join(", ")}</span>
        ) : null}
        {Number.isFinite(Number(event.impact_magnitude)) ? (
          <span>Impact {formatSignedNumber(event.impact_magnitude)}</span>
        ) : null}
        {Number.isFinite(Number(event.confidence)) ? <span>Confidence {formatPercent(event.confidence)}</span> : null}
      </div>
    </div>
  );
}

function EvidenceSourceCard({ source }) {
  const publishedAt = source.published_at || source.ingested_at;
  return (
    <div className="rounded-lg border border-borderDefault bg-backgroundBody/60 px-3 py-2">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="rounded bg-secondary/50 px-1.5 py-0.5 text-[10px] font-semibold text-textSecondary">
              {source.provider || "Source"}
            </span>
            <span className="text-[11px] text-textSecondary">#{source.id}</span>
          </div>
          <p className="mt-1 text-sm font-medium text-textPrimary">{source.title || "-"}</p>
        </div>
        {source.url ? (
          <a
            href={source.url}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-xs text-primary hover:underline"
          >
            Open
          </a>
        ) : null}
      </div>
      {source.body_excerpt ? (
        <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-textSecondary">{stripHtml(source.body_excerpt)}</p>
      ) : null}
      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-textSecondary">
        {source.source_name ? <span>{source.source_name}</span> : null}
        {publishedAt ? <span>{formatTime(publishedAt)}</span> : null}
        {source.stream_profile ? <span>{labelize(source.stream_profile)}</span> : null}
        {Number.isFinite(Number(source.materiality_score)) ? (
          <span>Materiality {formatScore(source.materiality_score)}</span>
        ) : null}
      </div>
    </div>
  );
}

export default function MarketMindPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [snapshots, setSnapshots] = useState([]);
  const [selectedSnapshotKey, setSelectedSnapshotKey] = useState(
    () => searchParams.get("selected") || searchParams.get("expanded") || ""
  );
  const [activeDetailTab, setActiveDetailTab] = useState(() => {
    const tab = searchParams.get("detail") || "overview";
    return DETAIL_TABS.some((item) => item.id === tab) ? tab : "overview";
  });
  const [page, setPage] = useState(() => parsePositiveInt(searchParams.get("page"), 1));
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [targetType, setTargetType] = useState(() => searchParams.get("target_type") || "");
  const [targetKey, setTargetKey] = useState(() => searchParams.get("target_key") || "");
  const [timeframe, setTimeframe] = useState(() =>
    optionValueOrDefault(TIMEFRAME_OPTIONS, searchParams.get("timeframe") || "")
  );
  const [freshness, setFreshness] = useState(() =>
    optionValueOrDefault(FRESHNESS_OPTIONS, searchParams.get("stale") || "")
  );
  const [minConfidence, setMinConfidence] = useState(() =>
    optionValueOrDefault(CONFIDENCE_OPTIONS, searchParams.get("min_confidence") || "")
  );
  const [ordering, setOrdering] = useState(() =>
    optionValueOrDefault(ORDERING_OPTIONS, searchParams.get("ordering") || "-computed_at", "-computed_at")
  );
  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  const [snapshotEvidence, setSnapshotEvidence] = useState({});
  const [copiedSnapshotId, setCopiedSnapshotId] = useState(null);

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  const selectedEntry = useMemo(() => {
    if (snapshots.length === 0) return null;
    const exactIndex = snapshots.findIndex((snapshot, index) => snapshotRouteKey(snapshot, index) === selectedSnapshotKey);
    const index = exactIndex >= 0 ? exactIndex : 0;
    const snapshot = snapshots[index];
    return {
      index,
      key: snapshotRouteKey(snapshot, index),
      snapshot,
    };
  }, [selectedSnapshotKey, snapshots]);

  const selectedSnapshot = selectedEntry?.snapshot || null;
  const selectedIndex = selectedEntry?.index ?? -1;
  const selectedKey = selectedEntry?.key || "";
  const hasPreviousSnapshot = selectedIndex > 0;
  const hasNextSnapshot = selectedIndex >= 0 && selectedIndex < snapshots.length - 1;

  const pageStats = useMemo(() => {
    const stats = {
      total: snapshots.length,
      fresh: 0,
      stale: 0,
      bullish: 0,
      bearish: 0,
      mixed: 0,
      neutral: 0,
      confidenceSum: 0,
      confidenceCount: 0,
    };

    snapshots.forEach((snapshot) => {
      const bias = getSnapshotBias(snapshot);
      if (snapshot.is_stale) stats.stale += 1;
      else stats.fresh += 1;
      if (bias === "BULLISH") stats.bullish += 1;
      else if (bias === "BEARISH") stats.bearish += 1;
      else if (bias === "MIXED") stats.mixed += 1;
      else stats.neutral += 1;
      const confidence = Number(snapshot?.active_thesis?.confidence ?? snapshot?.confidence);
      if (Number.isFinite(confidence)) {
        stats.confidenceSum += confidence;
        stats.confidenceCount += 1;
      }
    });

    return stats;
  }, [snapshots]);

  const averageConfidence =
    pageStats.confidenceCount > 0 ? formatPercent(pageStats.confidenceSum / pageStats.confidenceCount) : "-";

  const buildRouteParams = (overrides = {}) => {
    const nextPage = overrides.page ?? page;
    const nextTargetType = overrides.target_type ?? targetType;
    const nextTargetKey = overrides.target_key ?? targetKey;
    const nextTimeframe = overrides.timeframe ?? timeframe;
    const nextFreshness = overrides.stale ?? freshness;
    const nextMinConfidence = overrides.min_confidence ?? minConfidence;
    const nextOrdering = overrides.ordering ?? ordering;
    const nextSearch = overrides.search ?? search;
    const nextSelected = overrides.selected ?? selectedSnapshotKey;
    const nextDetail = overrides.detail ?? activeDetailTab;
    const params = new URLSearchParams();

    if (nextPage > 1) params.set("page", String(nextPage));
    if (nextTargetType) params.set("target_type", nextTargetType);
    if (nextTargetKey) params.set("target_key", nextTargetKey);
    if (nextTimeframe) params.set("timeframe", nextTimeframe);
    if (nextFreshness) params.set("stale", nextFreshness);
    if (nextMinConfidence) params.set("min_confidence", nextMinConfidence);
    if (nextOrdering && nextOrdering !== "-computed_at") params.set("ordering", nextOrdering);
    if (nextSearch) params.set("search", nextSearch);
    if (nextSelected) params.set("selected", nextSelected);
    if (nextDetail && nextDetail !== "overview") params.set("detail", nextDetail);

    return params;
  };

  const syncRouteParams = (overrides = {}) => {
    setSearchParams(buildRouteParams(overrides), { replace: true });
  };

  const fetchSnapshots = async (overrides = {}) => {
    const currentPage = overrides.page ?? page;
    const currentTargetType = overrides.target_type ?? targetType;
    const currentTargetKey = overrides.target_key ?? targetKey;
    const currentTimeframe = overrides.timeframe ?? timeframe;
    const currentFreshness = overrides.stale ?? freshness;
    const currentMinConfidence = overrides.min_confidence ?? minConfidence;
    const currentOrdering = overrides.ordering ?? ordering;
    const currentSearch = overrides.search ?? search;

    try {
      setLoading(true);
      setError(null);
      const res = await listMarketStateSnapshots({
        target_type: currentTargetType || undefined,
        target_key: currentTargetKey || undefined,
        timeframe: currentTimeframe || undefined,
        stale: currentFreshness || undefined,
        min_confidence: currentMinConfidence || undefined,
        ordering: currentOrdering || "-computed_at",
        search: currentSearch || undefined,
        page: currentPage,
        page_size: pageSize,
      });
      const items = Array.isArray(res?.results) ? res.results : Array.isArray(res) ? res : [];
      setSnapshots(items);
      setTotalCount(typeof res?.count === "number" ? res.count : items.length);

      const preferredSelected = overrides.selected ?? selectedSnapshotKey;
      const selectedExists = items.some((snapshot, index) => snapshotRouteKey(snapshot, index) === preferredSelected);
      const nextSelected = selectedExists ? preferredSelected : items.length > 0 ? snapshotRouteKey(items[0], 0) : "";
      setSelectedSnapshotKey(nextSelected);
    } catch (e) {
      setError(e?.message || "Failed to load Market Mind snapshots.");
      setSnapshots([]);
      setTotalCount(0);
      setSelectedSnapshotKey("");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSnapshots({ page });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearFilters = () => {
    setTargetType("");
    setTargetKey("");
    setTimeframe("");
    setFreshness("");
    setMinConfidence("");
    setOrdering("-computed_at");
    setSearch("");
    setPage(1);
    setSelectedSnapshotKey("");
    syncRouteParams({
      page: 1,
      target_type: "",
      target_key: "",
      timeframe: "",
      stale: "",
      min_confidence: "",
      ordering: "-computed_at",
      search: "",
      selected: "",
    });
    fetchSnapshots({
      page: 1,
      target_type: "",
      target_key: "",
      timeframe: "",
      stale: "",
      min_confidence: "",
      ordering: "-computed_at",
      search: "",
      selected: "",
    });
  };

  const applyFilters = () => {
    setPage(1);
    setSelectedSnapshotKey("");
    syncRouteParams({ page: 1, selected: "" });
    fetchSnapshots({ page: 1, selected: "" });
  };

  const goToPage = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) return;
    setPage(nextPage);
    setSelectedSnapshotKey("");
    syncRouteParams({ page: nextPage, selected: "" });
    fetchSnapshots({ page: nextPage, selected: "" });
  };

  const selectSnapshot = (snapshot, index) => {
    const nextSelected = snapshotRouteKey(snapshot, index);
    setSelectedSnapshotKey(nextSelected);
    syncRouteParams({ selected: nextSelected });
  };

  const selectSnapshotByIndex = (nextIndex) => {
    if (nextIndex < 0 || nextIndex >= snapshots.length) return;
    selectSnapshot(snapshots[nextIndex], nextIndex);
  };

  const handleTargetListKeyDown = (event) => {
    if (snapshots.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      selectSnapshotByIndex(Math.min(snapshots.length - 1, selectedIndex + 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      selectSnapshotByIndex(Math.max(0, selectedIndex - 1));
    } else if (event.key === "Home") {
      event.preventDefault();
      selectSnapshotByIndex(0);
    } else if (event.key === "End") {
      event.preventDefault();
      selectSnapshotByIndex(snapshots.length - 1);
    }
  };

  const changeDetailTab = (tab) => {
    setActiveDetailTab(tab);
    syncRouteParams({ detail: tab });
  };

  const copySnapshotBrief = async (snapshot) => {
    const text = buildSnapshotBrief(snapshot);
    const textarea = document.createElement("textarea");
    textarea.value = text;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";

    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
      } else {
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
      }
      setCopiedSnapshotId(snapshot.id || snapshot.target_key || snapshot.target_name);
      window.setTimeout(() => setCopiedSnapshotId(null), 1600);
    } catch {
      setError("Could not copy the Market Mind brief.");
    } finally {
      if (textarea.parentNode) textarea.parentNode.removeChild(textarea);
    }
  };

  const loadSnapshotEvidence = async (key, snapshot, thesis, stateChange) => {
    const { eventIds, sourceIds } = collectSnapshotEvidenceIds(snapshot, thesis, stateChange);
    if (eventIds.length === 0 && sourceIds.length === 0) {
      setSnapshotEvidence((prev) => ({
        ...prev,
        [key]: { loading: false, loaded: true, error: null, events: [], sources: [] },
      }));
      return;
    }

    const current = snapshotEvidence[key];
    if (current?.loading || current?.loaded) return;

    setSnapshotEvidence((prev) => ({
      ...prev,
      [key]: { ...(prev[key] || {}), loading: true, error: null },
    }));

    try {
      const [eventsRes, sourcesRes] = await Promise.all([
        eventIds.length > 0
          ? getKnowledgeGraphEvents({ ids: eventIds, page_size: Math.min(500, eventIds.length) })
          : Promise.resolve({ results: [] }),
        sourceIds.length > 0
          ? listMarketIntelligenceItems({ ids: sourceIds, page_size: Math.min(500, sourceIds.length) })
          : Promise.resolve({ results: [] }),
      ]);

      const events = Array.isArray(eventsRes?.results) ? eventsRes.results : Array.isArray(eventsRes) ? eventsRes : [];
      const sources = Array.isArray(sourcesRes?.results) ? sourcesRes.results : Array.isArray(sourcesRes) ? sourcesRes : [];
      setSnapshotEvidence((prev) => ({
        ...prev,
        [key]: { loading: false, loaded: true, error: null, events, sources },
      }));
    } catch (e) {
      setSnapshotEvidence((prev) => ({
        ...prev,
        [key]: {
          loading: false,
          loaded: false,
          error: e?.message || "Failed to load evidence details.",
          events: [],
          sources: [],
        },
      }));
    }
  };

  useEffect(() => {
    if (activeDetailTab !== "evidence" || !selectedSnapshot || !selectedKey) return;
    const thesis = selectedSnapshot.active_thesis || null;
    const stateChange = thesis?.state_change || {};
    loadSnapshotEvidence(selectedKey, selectedSnapshot, thesis, stateChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDetailTab, selectedKey]);

  const renderDetail = () => {
    if (!selectedSnapshot) {
      return (
        <div className="flex min-h-[420px] items-center justify-center p-8 text-sm text-textSecondary">
          {loading ? "Loading Market Mind..." : "No Market Mind snapshots found."}
        </div>
      );
    }

    const thesis = selectedSnapshot.active_thesis || {};
    const stateChange = thesis.state_change || {};
    const bias = getSnapshotBias(selectedSnapshot);
    const confidence = thesis.confidence ?? selectedSnapshot.confidence;
    const counts = getSnapshotCounts(selectedSnapshot);
    const llmBrief = getSnapshotLlmBrief(selectedSnapshot);
    const evidenceIds = collectSnapshotEvidenceIds(selectedSnapshot, thesis, stateChange);
    const evidence = snapshotEvidence[selectedKey] || {};
    const isCopied = copiedSnapshotId === (selectedSnapshot.id || selectedSnapshot.target_key || selectedSnapshot.target_name);
    const graphUrl = knowledgeGraphUrlForSnapshot(selectedSnapshot);
    const entities = Array.isArray(selectedSnapshot.entities) ? selectedSnapshot.entities : [];
    const affectedAssets = Array.isArray(thesis.affected_assets) ? thesis.affected_assets : [];
    const supportingEventIds = Array.isArray(thesis.supporting_event_ids) ? thesis.supporting_event_ids : [];
    const contradictingEventIds = Array.isArray(thesis.contradicting_event_ids) ? thesis.contradicting_event_ids : [];
    const triggerEventIds = Array.isArray(stateChange.trigger_event_ids) ? stateChange.trigger_event_ids : [];
    const snapshotSummary = String(selectedSnapshot.summary || "").trim();

    return (
      <>
        <div className="border-b border-borderDefault p-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-xl font-semibold text-textPrimary">
                  {selectedSnapshot.target_name || selectedSnapshot.target_key || "Untitled target"}
                </h2>
                <span className="rounded-full bg-primary/15 px-2 py-0.5 text-[11px] font-semibold text-primary">
                  {labelize(selectedSnapshot.target_type)}
                </span>
                <span className={biasBadgeClass(bias)}>{labelize(bias)}</span>
                <span className={freshnessBadgeClass(selectedSnapshot.is_stale)}>
                  {selectedSnapshot.is_stale ? "Stale" : "Fresh"}
                </span>
              </div>
              {llmBrief ? (
                <div className="mt-3 max-w-4xl rounded-lg border border-primary/20 bg-primary/5 p-3">
                  <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-primary">
                    <span>Market brief</span>
                    {llmBrief.model_name ? <span className="text-textSecondary">{llmBrief.model_name}</span> : null}
                    {llmBrief.generated_at ? <span className="text-textSecondary">{formatAge(llmBrief.generated_at)}</span> : null}
                  </div>
                  <p className="mt-2 text-sm leading-relaxed text-textPrimary">{llmBrief.text}</p>
                </div>
              ) : (
                <p className="mt-2 max-w-4xl text-sm leading-relaxed text-textSecondary">
                  {snapshotSummary || thesis.description || "No market brief available for this snapshot yet."}
                </p>
              )}
              {llmBrief && snapshotSummary ? (
                <div className="mt-3 max-w-4xl rounded-lg border border-borderDefault bg-backgroundBody/40 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-textSecondary">
                    Snapshot summary
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-textSecondary">{snapshotSummary}</p>
                </div>
              ) : null}
              <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-textSecondary">
                <span>Key: {selectedSnapshot.target_key || "-"}</span>
                <span>Timeframe: {labelize(selectedSnapshot.timeframe || "latest")}</span>
                <span>Computed: {formatAge(selectedSnapshot.computed_at)}</span>
                <span>Stale after: {selectedSnapshot.stale_after ? formatTime(selectedSnapshot.stale_after) : "-"}</span>
              </div>
            </div>

            <div className="flex shrink-0 flex-wrap items-center gap-2">
              <div className="inline-flex items-center rounded-lg border border-borderDefault bg-backgroundBody/50">
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-r-none px-2"
                  onClick={() => selectSnapshotByIndex(selectedIndex - 1)}
                  disabled={!hasPreviousSnapshot}
                  title="Previous target"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="border-x border-borderDefault px-2 text-xs text-textSecondary">
                  {selectedIndex + 1} / {snapshots.length}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  className="rounded-l-none px-2"
                  onClick={() => selectSnapshotByIndex(selectedIndex + 1)}
                  disabled={!hasNextSnapshot}
                  title="Next target"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
              <Button size="sm" variant="outline" onClick={() => copySnapshotBrief(selectedSnapshot)}>
                <Clipboard className="mr-1.5 h-4 w-4" />
                {isCopied ? "Copied" : "Copy brief"}
              </Button>
              <a
                href={graphUrl}
                className="inline-flex items-center justify-center rounded-lg border border-borderDefault bg-transparent px-2 py-1 text-sm font-medium text-textPrimary transition-colors hover:bg-secondary/20"
              >
                <ExternalLink className="mr-1.5 h-4 w-4" />
                Graph
              </a>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
            <DecisionMetric
              icon={BrainCircuit}
              label="Thesis"
              value={thesis.title || "No active thesis"}
              detail={thesis.status ? labelize(thesis.status) : undefined}
              valueClassName={biasTextClass(bias)}
            />
            <DecisionMetric
              icon={BarChart3}
              label="Confidence"
              value={formatPercent(confidence)}
              detail={`Sentiment ${formatSignedNumber(selectedSnapshot.sentiment_score)}`}
              valueClassName={biasTextClass(bias)}
            />
            <DecisionMetric
              icon={Layers}
              label="Evidence"
              value={`${counts.eventCount} events`}
              detail={`${counts.clusterCount} clusters, ${counts.sourceCount} sources`}
            />
            <DecisionMetric
              icon={Clock3}
              label="Updated"
              value={formatAge(selectedSnapshot.computed_at)}
              detail={formatTime(selectedSnapshot.computed_at)}
            />
          </div>
        </div>

        <div className="border-b border-borderDefault px-4 py-2">
          <div className="flex flex-wrap gap-1">
            {DETAIL_TABS.map((tab) => {
              const Icon = tab.icon;
              const active = activeDetailTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => changeDetailTab(tab.id)}
                  className={[
                    "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition",
                    active
                      ? "bg-primary text-textAccent"
                      : "text-textSecondary hover:bg-secondary/30 hover:text-textPrimary",
                  ].join(" ")}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4">
          {activeDetailTab === "overview" ? (
            <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
              <div className="space-y-4">
                <div className="rounded-lg border border-borderDefault bg-backgroundBody/40 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-sm font-semibold text-textPrimary">{thesis.title || "Current thesis"}</h3>
                    {thesis.status ? <span className={thesisStatusBadgeClass(thesis.status)}>{labelize(thesis.status)}</span> : null}
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-textSecondary">
                    {thesis.description || selectedSnapshot.summary || "No thesis description available."}
                  </p>
                  {stateChange.reason ? (
                    <div className="mt-4 rounded-lg border border-warning/30 bg-warning/10 p-3">
                      <div className="text-xs font-semibold uppercase text-warning">Latest Change</div>
                      <p className="mt-1 text-sm leading-relaxed text-textPrimary">{stateChange.reason}</p>
                      <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-textSecondary">
                        {stateChange.changed_at ? <span>{formatTime(stateChange.changed_at)}</span> : null}
                        {stateChange.previous_bias ? <span>From {labelize(stateChange.previous_bias)}</span> : null}
                        {stateChange.new_bias ? <span>To {labelize(stateChange.new_bias)}</span> : null}
                      </div>
                    </div>
                  ) : null}
                </div>

                <div className="grid gap-3 lg:grid-cols-2">
                  <SnapshotSection
                    title="Bullish Factors"
                    items={selectedSnapshot.bullish_factors}
                    emptyLabel="No bullish factors captured in this window."
                    tone="bullish"
                    limit={3}
                  />
                  <SnapshotSection
                    title="Bearish Factors"
                    items={selectedSnapshot.bearish_factors}
                    emptyLabel="No bearish factors captured in this window."
                    tone="bearish"
                    limit={3}
                  />
                </div>
              </div>

              <div className="space-y-4">
                <div className="rounded-lg border border-borderDefault bg-backgroundBody/40 p-3">
                  <h3 className="text-xs font-semibold uppercase text-textSecondary">Linked Context</h3>
                  <div className="mt-3 space-y-3">
                    <div>
                      <div className="text-[11px] font-semibold uppercase text-textSecondary">Assets</div>
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {(affectedAssets.length > 0 ? affectedAssets : [selectedSnapshot.target_key].filter(Boolean))
                          .slice(0, 12)
                          .map((asset) => (
                            <span
                              key={asset}
                              className="rounded border border-borderDefault bg-backgroundSurface px-2 py-0.5 text-[11px] text-textSecondary"
                            >
                              {asset}
                            </span>
                          ))}
                      </div>
                    </div>
                    {entities.length > 0 ? (
                      <div>
                        <div className="text-[11px] font-semibold uppercase text-textSecondary">Entities</div>
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {entities.slice(0, 12).map((entity) => (
                            <span
                              key={entity.id || entity.entity_key || entity.name}
                              className="rounded border border-borderDefault bg-backgroundSurface px-2 py-0.5 text-[11px] text-textSecondary"
                            >
                              {entity.name || entity.entity_key}
                            </span>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>

                <SnapshotSection
                  title="Active Risks"
                  items={selectedSnapshot.active_risks}
                  emptyLabel="No active risks captured in this window."
                  tone="risk"
                  limit={3}
                />
                <SnapshotSection
                  title="Upcoming Catalysts"
                  items={selectedSnapshot.upcoming_catalysts}
                  emptyLabel="No upcoming catalysts inferred for this window."
                  tone="catalyst"
                  variant="catalyst"
                  limit={3}
                />
              </div>
            </div>
          ) : null}

          {activeDetailTab === "signals" ? (
            <div className="grid gap-3 lg:grid-cols-2">
              <SnapshotSection
                title="Bullish Factors"
                items={selectedSnapshot.bullish_factors}
                emptyLabel="No bullish factors captured in this window."
                tone="bullish"
              />
              <SnapshotSection
                title="Bearish Factors"
                items={selectedSnapshot.bearish_factors}
                emptyLabel="No bearish factors captured in this window."
                tone="bearish"
              />
              <SnapshotSection
                title="Active Risks"
                items={selectedSnapshot.active_risks}
                emptyLabel="No active risks captured in this window."
                tone="risk"
              />
              <SnapshotSection
                title="Upcoming Catalysts"
                items={selectedSnapshot.upcoming_catalysts}
                emptyLabel="No upcoming catalysts inferred for this window."
                tone="catalyst"
                variant="catalyst"
              />
              <div className="lg:col-span-2">
                <SnapshotSection
                  title="Recent Changes"
                  items={selectedSnapshot.recent_changes}
                  emptyLabel="No recent changes captured in this snapshot."
                />
              </div>
            </div>
          ) : null}

          {activeDetailTab === "changes" ? (
            <div className="grid gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
              <div className="space-y-3">
                <div className="rounded-lg border border-borderDefault bg-backgroundBody/40 p-4">
                  <h3 className="text-sm font-semibold text-textPrimary">Latest Thesis Change</h3>
                  {stateChange.reason ? (
                    <>
                      <p className="mt-3 text-sm leading-relaxed text-textSecondary">{stateChange.reason}</p>
                      <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                        <div className="rounded-md bg-backgroundSurface px-3 py-2">
                          <div className="text-textSecondary">Previous</div>
                          <div className="mt-1 font-semibold text-textPrimary">{labelize(stateChange.previous_bias)}</div>
                        </div>
                        <div className="rounded-md bg-backgroundSurface px-3 py-2">
                          <div className="text-textSecondary">Current</div>
                          <div className={`mt-1 font-semibold ${biasTextClass(stateChange.new_bias || bias)}`}>
                            {labelize(stateChange.new_bias || bias)}
                          </div>
                        </div>
                      </div>
                      <div className="mt-3 text-xs text-textSecondary">
                        {stateChange.changed_at ? formatTime(stateChange.changed_at) : "Change time unavailable"}
                      </div>
                    </>
                  ) : (
                    <p className="mt-3 text-sm text-textSecondary">No thesis transition is stored for this snapshot.</p>
                  )}
                </div>

                <div className="rounded-lg border border-borderDefault bg-backgroundBody/40 p-4">
                  <EvidenceChipList label="Trigger Events" ids={triggerEventIds} tone="trigger" />
                  {triggerEventIds.length === 0 ? (
                    <p className="text-sm text-textSecondary">No trigger event IDs were stored for this change.</p>
                  ) : null}
                </div>
              </div>

              <SnapshotSection
                title="Recent Changes"
                items={selectedSnapshot.recent_changes}
                emptyLabel="No recent changes captured in this snapshot."
              />
            </div>
          ) : null}

          {activeDetailTab === "evidence" ? (
            <div className="space-y-4">
              <div className="grid gap-3 lg:grid-cols-4">
                <EvidenceChipList label="Key events" ids={selectedSnapshot.key_event_ids} />
                <EvidenceChipList label="Supporting" ids={supportingEventIds} tone="supporting" />
                <EvidenceChipList label="Contradicting" ids={contradictingEventIds} tone="contradicting" />
                <EvidenceChipList label="Triggers" ids={triggerEventIds} tone="trigger" />
              </div>

              {evidenceIds.eventIds.length === 0 && evidenceIds.sourceIds.length === 0 ? (
                <div className="rounded-lg border border-borderDefault bg-backgroundBody/40 p-4 text-sm text-textSecondary">
                  No linked event or source IDs were stored for this snapshot.
                </div>
              ) : null}

              {evidence.loading ? (
                <div className="rounded-lg border border-borderDefault bg-backgroundBody/40 p-4 text-sm text-textSecondary">
                  Loading evidence...
                </div>
              ) : null}
              {evidence.error ? (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-4 text-sm text-destructive">
                  {evidence.error}
                </div>
              ) : null}

              <div className="grid gap-4 xl:grid-cols-2">
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-textPrimary">Events</h3>
                    <span className="text-xs text-textSecondary">{evidence.events?.length || 0}</span>
                  </div>
                  <div className="space-y-2">
                    {Array.isArray(evidence.events) && evidence.events.length > 0 ? (
                      evidence.events.map((event) => <EvidenceEventCard key={event.id} event={event} />)
                    ) : !evidence.loading ? (
                      <div className="rounded-lg border border-borderDefault bg-backgroundBody/40 p-3 text-sm text-textSecondary">
                        No event details loaded.
                      </div>
                    ) : null}
                  </div>
                </div>
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-textPrimary">Sources</h3>
                    <span className="text-xs text-textSecondary">{evidence.sources?.length || 0}</span>
                  </div>
                  <div className="space-y-2">
                    {Array.isArray(evidence.sources) && evidence.sources.length > 0 ? (
                      evidence.sources.map((source) => <EvidenceSourceCard key={source.id} source={source} />)
                    ) : !evidence.loading ? (
                      <div className="rounded-lg border border-borderDefault bg-backgroundBody/40 p-3 text-sm text-textSecondary">
                        No source details loaded.
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </>
    );
  };

  return (
    <div className="mx-auto max-w-[1500px] space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="flex items-start gap-3">
          <PageBackButton label="Back to Research" to="/research" preferHistory={false} hideLabelOnMobile />
          <div>
            <h1 className="text-2xl font-semibold text-textPrimary">Market Mind</h1>
            <p className="mt-1 text-sm text-textSecondary">
              Current thesis, confidence, risks, catalysts, and evidence for watched markets.
            </p>
          </div>
        </div>
        <Button size="sm" variant="outline" onClick={() => fetchSnapshots({ page })} disabled={loading}>
          <RefreshCw className={`mr-1.5 h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      <Card className="rounded-lg p-3">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-[1fr_1fr_1fr_1fr_1fr_1fr_1.4fr_auto]">
          <div>
            <label className="block text-xs font-semibold uppercase text-textSecondary">Target Type</label>
            <select className={`${selectClassName} mt-1`} value={targetType} onChange={(e) => setTargetType(e.target.value)}>
              {TARGET_TYPES.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-textSecondary">Target Key</label>
            <Input
              className="mt-1"
              placeholder="USD, XAU, inflation"
              value={targetKey}
              onChange={(e) => setTargetKey(e.target.value)}
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-textSecondary">Timeframe</label>
            <select className={`${selectClassName} mt-1`} value={timeframe} onChange={(e) => setTimeframe(e.target.value)}>
              {TIMEFRAME_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-textSecondary">Freshness</label>
            <select className={`${selectClassName} mt-1`} value={freshness} onChange={(e) => setFreshness(e.target.value)}>
              {FRESHNESS_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-textSecondary">Confidence</label>
            <select
              className={`${selectClassName} mt-1`}
              value={minConfidence}
              onChange={(e) => setMinConfidence(e.target.value)}
            >
              {CONFIDENCE_OPTIONS.map((option) => (
                <option key={option.value || "all"} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-textSecondary">Sort</label>
            <select className={`${selectClassName} mt-1`} value={ordering} onChange={(e) => setOrdering(e.target.value)}>
              {ORDERING_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase text-textSecondary">Search</label>
            <div className="relative mt-1">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textSecondary" />
              <Input
                className="pl-9"
                placeholder="Search targets or thesis"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-end gap-2">
            <Button className="h-10" onClick={applyFilters}>
              Apply
            </Button>
            <Button className="h-10" variant="secondary" onClick={clearFilters}>
              Clear
            </Button>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        <MetricCard icon={Target} label="Targets" value={pageStats.total} detail={`${totalCount} total results`} />
        <MetricCard icon={Clock3} label="Fresh" value={pageStats.fresh} detail={`${pageStats.stale} stale`} tone="success" />
        <MetricCard icon={TrendingUp} label="Bullish" value={pageStats.bullish} detail={`${pageStats.mixed} mixed`} tone="success" />
        <MetricCard icon={TrendingDown} label="Bearish" value={pageStats.bearish} detail={`${pageStats.neutral} neutral`} tone="danger" />
        <MetricCard icon={Activity} label="Avg Confidence" value={averageConfidence} detail={`${pageStats.mixed} mixed, ${pageStats.neutral} neutral`} />
      </div>

      <div className="grid gap-4 xl:grid-cols-[360px_minmax(0,1fr)]">
        <Card className="overflow-hidden rounded-lg xl:sticky xl:top-4 xl:self-start">
          <div className="border-b border-borderDefault px-4 py-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h2 className="text-sm font-semibold text-textPrimary">Targets</h2>
                <p className="text-xs text-textSecondary">Select one thesis to inspect.</p>
              </div>
              <span className="rounded-full bg-secondary/40 px-2 py-0.5 text-xs text-textSecondary">
                Page {page}
              </span>
            </div>
          </div>

          <div
            className="min-h-[360px] max-h-[calc(100vh-340px)] overflow-y-auto focus:outline-none focus:ring-2 focus:ring-primary/30"
            tabIndex={0}
            onKeyDown={handleTargetListKeyDown}
            aria-label="Market Mind targets"
          >
            {loading && snapshots.length === 0 ? (
              <div className="px-4 py-8 text-sm text-textSecondary">Loading snapshots...</div>
            ) : null}
            {error && !loading ? <div className="px-4 py-8 text-sm text-destructive">{error}</div> : null}
            {!loading && !error && snapshots.length === 0 ? (
              <div className="px-4 py-8 text-sm text-textSecondary">No snapshots match these filters.</div>
            ) : null}
            {snapshots.map((snapshot, index) => {
              const key = snapshotRouteKey(snapshot, index);
              return (
                <SnapshotListItem
                  key={key}
                  snapshot={snapshot}
                  index={index}
                  selected={key === selectedKey}
                  onSelect={selectSnapshot}
                />
              );
            })}
          </div>

          <div className="flex items-center justify-between border-t border-borderDefault px-3 py-3">
            <Button size="sm" variant="outline" onClick={() => goToPage(page - 1)} disabled={page <= 1 || loading}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-xs text-textSecondary">
              {page} of {totalPages}
            </span>
            <Button size="sm" variant="outline" onClick={() => goToPage(page + 1)} disabled={page >= totalPages || loading}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </Card>

        <Card className="min-h-[560px] overflow-hidden rounded-lg">
          {renderDetail()}
        </Card>
      </div>
    </div>
  );
}
